import React, { useState } from 'react';

import { DEFAULT_VENDOR_TO_ID } from '@silverfort-nodelibs/common/utils/consts';

import { httpPost } from '../../../utils';
import TextInput from '../Controls/TextInput/TextInput';
import UploadButton from '../Controls/UploadButton/UploadButton';

import styles from './SSOApps.module.css';
import { SSO_APPLICATION_FIELDS } from '../../common/fields';
import LoginHint from './LoginHint';
import { SUBCATEGORIES } from '../../common/categories';
import { SettingsFeatureLock } from '../LockedFeatures/SettingsFeatureLock';

const { VENDOR_ID } = SSO_APPLICATION_FIELDS.CUSTOM_VENDOR;
const { SSO_APPS } = SUBCATEGORIES;

const uploadCertificate = async (files, fieldId, changed, setStatus) => {
    setStatus('');
    const file = files[0];
    const requestUrl = '/Settings/UploadAzureEnterpriseAppCert';
    const data = new FormData();
    data.append('file', file);

    try {
        const response = await httpPost(requestUrl, data);
        const { certificateData } = response.data;
        if (!certificateData) {
            return Promise.reject('No certificate data');
        }

        changed(fieldId, certificateData);
    } catch (err) {
        console.error(err);
        setStatus('error');
        changed(fieldId, null);
    }
};

const getSSOApplicationFieldsByVendor = (vendorId) => {
    let fields;
    switch (vendorId) {
        case DEFAULT_VENDOR_TO_ID.AZURE:
            fields = SSO_APPLICATION_FIELDS.ENTRA_ID;
            break;
        case DEFAULT_VENDOR_TO_ID.OKTA:
            fields = SSO_APPLICATION_FIELDS.OKTA;
            break;
        case DEFAULT_VENDOR_TO_ID.PING_FEDERATE:
            fields = SSO_APPLICATION_FIELDS.PING_FEDERATE;
            break;
        case DEFAULT_VENDOR_TO_ID.ONE_LOGIN:
            fields = SSO_APPLICATION_FIELDS.ONE_LOGIN;
            break;
        case DEFAULT_VENDOR_TO_ID.CYBER_ARK:
            fields = SSO_APPLICATION_FIELDS.CYBER_ARK;
            break;
        case DEFAULT_VENDOR_TO_ID.THALES_STA:
            fields = SSO_APPLICATION_FIELDS.THALES_STA;
            break;
        case DEFAULT_VENDOR_TO_ID.BEYOND_IDENTITY:
            fields = SSO_APPLICATION_FIELDS.BEYOND_IDENTITY;
            break;
        default:
            fields = SSO_APPLICATION_FIELDS.CUSTOM_VENDOR;
            break;
    }
    const { APP_NAME, LOGIN_URL, AUDIENCE, CERTIFICATE, ISSUER, VENDOR_NAME, VENDOR_ID, LOGIN_HINT } = fields;
    return { APP_NAME, LOGIN_URL, AUDIENCE, CERTIFICATE, ISSUER, VENDOR_NAME, VENDOR_ID, LOGIN_HINT };
};

const SSOApp = ({
    app,
    changedFunc,
    isVendorFieldDisabled = false,
    isVendorFieldVisible = false,
    disabled = false
}) => {
    const [status, setStatus] = useState();
    const { APP_NAME, LOGIN_URL, AUDIENCE, CERTIFICATE, ISSUER, VENDOR_NAME, LOGIN_HINT } =
        getSSOApplicationFieldsByVendor(app[VENDOR_ID.ID]);

    return (
        <div className={`${styles.cardContainer}${disabled ? ' ' + styles.disabled : ''}`}>
            {isVendorFieldVisible && (
                <TextInput
                    field={app[VENDOR_NAME.ID]}
                    color="grey"
                    changed={changedFunc}
                    disabled={isVendorFieldDisabled || disabled}
                    featureLockId={SSO_APPS.ID}
                    featureLockIsTabId={true}
                />
            )}
            <TextInput
                field={app[APP_NAME.ID]}
                color="grey"
                changed={changedFunc}
                disabled={disabled}
                featureLockId={SSO_APPS.ID}
                featureLockIsTabId={true}
            />
            <TextInput
                field={app[LOGIN_URL.ID]}
                color="grey"
                changed={changedFunc}
                disabled={disabled}
                featureLockId={SSO_APPS.ID}
                featureLockIsTabId={true}
            />
            <TextInput
                field={app[AUDIENCE.ID]}
                color="grey"
                changed={changedFunc}
                disabled={disabled}
                featureLockId={SSO_APPS.ID}
                featureLockIsTabId={true}
            />
            <TextInput
                field={app[ISSUER.ID]}
                color="grey"
                changed={changedFunc}
                disabled={disabled}
                featureLockId={SSO_APPS.ID}
                featureLockIsTabId={true}
            />
            {app[LOGIN_HINT.ID] && (
                <LoginHint field={app[LOGIN_HINT.ID]} changedFunc={changedFunc} disabled={disabled} />
            )}

            <div>
                <SettingsFeatureLock id={SSO_APPS.ID} isTabId={true}>
                    <UploadButton
                        disabled={disabled}
                        text={`${app[CERTIFICATE.ID].value ? 'Change' : 'Upload'} Certificate`}
                        onUpload={(files) => uploadCertificate(files, CERTIFICATE.ID, changedFunc, setStatus)}
                    />
                </SettingsFeatureLock>
                {status === 'error' && <div className={styles.errorMessage}>Failed to upload certificate</div>}
                {app[CERTIFICATE.ID].error && status !== 'error' && (
                    <div className={styles.errorMessage}>{app[CERTIFICATE.ID].error}</div>
                )}

                {status === 'success' && app[CERTIFICATE.ID].value && (
                    <div className={styles.successMessage}>Certificate has been uploaded successfully</div>
                )}
            </div>
        </div>
    );
};

export default SSOApp;
